import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Scroll-lock bookkeeping (backported from Horizon 4.x).
 *
 * A section re-render can remove or displace an open modal dialog. When that
 * happens the dialog never fires a close event, so without a watchdog the
 * body stays `position: fixed` and the page is frozen until a manual refresh.
 */
const scrollLockOwners = new Set();

/** @type {MutationObserver | undefined} */
let scrollLockObserver;

/**
 * Locks page scrolling on behalf of a dialog element.
 * @param {HTMLDialogElement} owner - The dialog that owns the lock.
 */
function lockScroll(owner) {
  if (scrollLockOwners.size === 0) {
    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  scrollLockOwners.add(owner);

  if (!scrollLockObserver) {
    scrollLockObserver = new MutationObserver(pruneScrollLockOwners);
    scrollLockObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
}

/**
 * Releases a dialog's scroll lock. Restores scrolling once no owners remain.
 * @param {HTMLDialogElement} owner - The dialog that owned the lock.
 */
function unlockScroll(owner) {
  if (!scrollLockOwners.delete(owner)) return;

  if (scrollLockOwners.size === 0) {
    scrollLockObserver?.disconnect();
    scrollLockObserver = undefined;

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';

    if (!document.querySelector('dialog[scroll-lock][open], details[scroll-lock][open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    if (scrollY) window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });
  }
}

/** Releases locks whose owner was removed from the DOM or lost its modal state. */
function pruneScrollLockOwners() {
  for (const owner of scrollLockOwners) {
    if (!owner.isConnected) {
      // The dialog was removed while open: clear the stuck `open` state so it
      // can be shown again if it gets re-inserted.
      owner.close();
      unlockScroll(owner);
    }
  }
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    const dialog = this.refs.dialog;
    if (dialog) unlockScroll(dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) {
      // Self-heal: if a re-render displaced the dialog from the top layer it
      // still has `open` set but is no longer modal. Reset it so it can be
      // shown properly instead of silently doing nothing.
      if (dialog.matches(':modal')) return;
      dialog.close();
      unlockScroll(dialog);
    }

    lockScroll(dialog);

    try {
      dialog.showModal();
    } catch (error) {
      unlockScroll(dialog);
      throw error;
    }

    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    try {
      dialog.classList.add('dialog-closing');

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });
    } finally {
      unlockScroll(dialog);

      dialog.close();
      dialog.classList.remove('dialog-closing');
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
